import { fromJS, List as ImmutableList, Map as ImmutableMap } from 'immutable'
import decamelizeKeysDeep from 'decamelize-keys-deep'
import { v4 } from 'uuid'

import { ChartDataSources, ChartTypes, EmptyAlert, EmptyAlertChart } from 'static/constants'
import { isPressrelationsNewsChart } from 'utils/data_source'
import { applyActionTemplates, syncSavedSearchId } from 'components/alerts/edit_dialog/helpers'

export const VISUAL_VIPER_CHART_TYPES = [
  ChartTypes.BAR,
  ChartTypes.HORIZONTAL_BAR,
  ChartTypes.PIE,
  ChartTypes.LINE,
  ChartTypes.AREA,
  ChartTypes.BUBBLE,
  ChartTypes.RADAR,
  ChartTypes.FUNNEL,
  ChartTypes.HEATMAP,
  ChartTypes.RECTANGLE,
  ChartTypes.GAUGE
]

const SKIP_FIRST_LEVEL_FLAGS = [
  'isNewsFeed',
  'isTopInfluencers',
  'isTopPublications',
  'isImageFeed',
  'isPageIdentities',
  'isPageIdentitiesTimeline',
  'isSavedSearchesTimeline'
]

const emptyList = fromJS([])
const emptyMap = fromJS({})

const visualViperChartType = type => (
  type === ChartTypes.HORIZONTAL_BAR ? 'horizontal_bar' : type
)

const matchesModule = (entity, moduleName) => {
  const moduleNames = entity.get('moduleNames')

  if (!moduleName || !moduleNames) {
    return true
  }

  return moduleNames.includes(moduleName)
}

const matchesDataSource = (entity, dataSource) => {
  const dataSources = entity.get('dataSources')

  if (!dataSources) {
    return true
  }

  return dataSources.includes(dataSource)
}

const isSkippedFirstLevel = (key, config) => {
  if (SKIP_FIRST_LEVEL_FLAGS.some(flag => config.get(flag))) {
    return true
  }

  return /savedSearch|feed|^top/i.test(key)
}

const chartOptsFromDashboardChart = chart => {
  const opts = chart.get('chartOpts') || chart.get('opts')

  if (ImmutableMap.isMap(opts)) {
    return opts.toJS()
  }

  return opts || {}
}

export const dashboardChartSavedSearchId = chart => (
  chart.get('savedSearchId')
  || chart.getIn(['savedSearch', 'id'])
  || chart.getIn(['dataSourceOpts', 'savedSearchId'])
  || null
)

export const isDashboardChartEligibleForAlert = (chart, configs) => {
  if (!chart || !isPressrelationsNewsChart(chart)) {
    return false
  }

  const bucket = chart.get('bucket') || chart.get('firstLevel')

  if (!bucket) {
    return false
  }

  const safeConfigs = ImmutableMap.isMap(configs) ? configs : fromJS(configs || {})

  return !isSkippedFirstLevel(bucket, safeConfigs.get(bucket) || emptyMap)
}

export const dashboardAlertEligibleCharts = (charts, configs) => {
  const list = ImmutableList.isList(charts) ? charts : fromJS(charts || [])

  return list.filter(chart => isDashboardChartEligibleForAlert(chart, configs))
}

const visualViperTypes = chartTypes => (
  (chartTypes || emptyMap)
    .keySeq()
    .filter(type => VISUAL_VIPER_CHART_TYPES.includes(type))
    .toList()
)

const levelNames = (levels, moduleName) => {
  const names = (levels || emptyList)
    .filter(level => matchesModule(level, moduleName))
    .map(level => level.get('name'))
    .filter(Boolean)
    .toJS()

  return names.filter((name, index) => names.indexOf(name) === index)
}

export const alertChartModuleName = (chart, savedSearches, fallbackSavedSearchId) => {
  const savedSearchId = chart.getIn(['dataSourceOpts', 'savedSearchId'])
    || chart.get('savedSearchId')
    || fallbackSavedSearchId

  if (!savedSearchId || !savedSearches) {
    return null
  }

  const savedSearch = savedSearches.find(search => search.get('id') === savedSearchId)

  return savedSearch ? savedSearch.get('moduleName') : null
}

export const alertChartOptions = (
  configs,
  moduleName,
  dataSource = ChartDataSources.PRESSRELATIONS_NEWS,
  selection = {}
) => {
  const safeConfigs = ImmutableMap.isMap(configs) ? configs : fromJS(configs || {})
  const buckets = safeConfigs
    .filter((config, key) => (
      !isSkippedFirstLevel(key, config)
      && matchesModule(config, moduleName)
      && matchesDataSource(config, dataSource)
      && !visualViperTypes(config.get('chartTypes')).isEmpty()
    ))
    .keySeq()
    .toList()
    .toJS()

  const requestedBucket = selection.bucket
  const bucket = buckets.includes(requestedBucket) ? requestedBucket : buckets[0]
  const chartTypes = safeConfigs.getIn([bucket, 'chartTypes'], emptyMap)
  const types = visualViperTypes(chartTypes).toJS()
  const requestedType = selection.type
  const type = types.includes(requestedType) ? requestedType : types[0]
  const typeConfig = chartTypes.get(type) || emptyMap

  return {
    buckets,
    types,
    groupBys: levelNames(typeConfig.get('secondLevels'), moduleName),
    metrics: levelNames(typeConfig.get('thirdLevels'), moduleName),
    bucket,
    type
  }
}

export const alignAlertChart = (chart, options) => {
  let next = chart

  if (options.bucket && chart.get('bucket') !== options.bucket) {
    next = next.set('bucket', options.bucket)
  }

  if (options.type && chart.get('type') !== options.type) {
    next = next.set('type', options.type)
  }

  const groupBy = next.get('groupBy')

  if (!groupBy || groupBy === 'ungrouped' || !(options.groupBys || []).includes(groupBy)) {
    next = next.set('groupBy', null)
  }

  const metric = next.get('metric')
  const nextMetric = (options.metrics || []).includes(metric)
    ? metric
    : ((options.metrics || [])[0] || null)

  if (metric !== nextMetric) {
    next = next.set('metric', nextMetric)
  }

  return next
}

export const applyAlertChartOptions = (chart, configs, moduleName, dataSource) => {
  const options = alertChartOptions(configs, moduleName, dataSource, {
    bucket: chart.get('bucket') || chart.get('firstLevel'),
    type: chart.get('type')
  })

  return alignAlertChart(chart, options)
}

export const alertChartFromDashboardChart = (chart, configs, savedSearches) => {
  const savedSearchId = dashboardChartSavedSearchId(chart)
  const dataSource = chart.get('dataSource') || ChartDataSources.PRESSRELATIONS_NEWS
  const type = VISUAL_VIPER_CHART_TYPES.includes(chart.get('type'))
    ? chart.get('type')
    : ChartTypes.BAR
  const next = fromJS({
    ...EmptyAlertChart,
    id: v4(),
    dataSource,
    dataSourceOpts: {
      savedSearchId,
      groupingType: chart.get('groupingType') || null,
      sortBy: chart.get('sortBy') || null
    },
    type,
    bucket: chart.get('bucket') || chart.get('firstLevel') || EmptyAlertChart.bucket,
    groupBy: chart.get('groupBy') || chart.get('secondLevel') || null,
    metric: chart.get('metric') || chart.get('thirdLevel') || null,
    dateRange: chart.get('dateRange') || EmptyAlertChart.dateRange,
    dateFrom: chart.get('dateFrom') || null,
    dateTo: chart.get('dateTo') || null,
    dateInterval: chart.get('dateInterval') || null,
    label: chart.get('label') || '',
    topLabel: chart.get('topLabel') || null,
    description: chart.get('description') || null,
    chartOpts: chartOptsFromDashboardChart(chart)
  })

  return applyAlertChartOptions(
    next,
    configs,
    alertChartModuleName(next, savedSearches, savedSearchId),
    dataSource
  )
}

export const alertFromDashboardCharts = (charts, configs, savedSearches) => {
  const list = ImmutableList.isList(charts) ? charts : fromJS(charts || [])

  if (list.isEmpty()) {
    return applyActionTemplates(fromJS(EmptyAlert))
  }

  const alertCharts = list.map(chart => (
    alertChartFromDashboardChart(chart.delete('data'), configs, savedSearches)
  ))

  return applyActionTemplates(
    syncSavedSearchId(fromJS(EmptyAlert), dashboardChartSavedSearchId(list.first()))
      .setIn(['actions', 0, 'charts'], fromJS(alertCharts.toJS()))
  )
}

export const alertFromDashboardChart = (chart, configs, savedSearches) => (
  alertFromDashboardCharts(fromJS([chart]), configs, savedSearches)
)

export const alertChartPreviewBody = (chart, { timezone, fallbackSavedSearchId, moduleName } = {}) => {
  const opts = (chart.get('dataSourceOpts') || fromJS({})).toJS()
  const savedSearchId = opts.savedSearchId
    || chart.get('savedSearchId')
    || fallbackSavedSearchId
    || null
  const size = opts.size || chart.getIn(['chartOpts', 'size']) || 10
  const chartOpts = decamelizeKeysDeep((chart.get('chartOpts') || fromJS({})).toJS())

  return {
    data_source: chart.get('dataSource') || ChartDataSources.PRESSRELATIONS_NEWS,
    data_source_opts: {
      saved_search_id: savedSearchId,
      size
    },
    bucket: chart.get('bucket') || chart.get('firstLevel') || null,
    group_by: chart.get('groupBy') || null,
    metric: chart.get('metric') || null,
    date_range: chart.get('dateRange') || null,
    date_from: chart.get('dateFrom') || null,
    date_to: chart.get('dateTo') || null,
    date_interval: chart.get('dateInterval') || null,
    timezone: timezone || 'Europe/Berlin',
    type: visualViperChartType(chart.get('type')),
    chart_opts: chartOpts,
    label: chart.get('label') || null,
    top_label: chart.get('topLabel') || null,
    module_name: moduleName || null
  }
}
